import {
  DEFAULT_FRONT_CLEARANCE,
  DEFAULT_FRONT_REVEAL,
  frontShape,
  HINGE_LABEL,
  type AssemblyInstance,
  type Front,
  type FrontHinge,
} from '@scs/domain-engine';
import { MmInput } from '../lib/ui';
import type { EditorStore } from './store';

type Exec = EditorStore['exec'];

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="prop-row">
      <label>{label}</label>
      {children}
    </div>
  );
}

export const HINGES: FrontHinge[] = ['left', 'right', 'top', 'bottom', 'none'];

/** Gap between neighbouring fronts (fuga): one setting for the whole front group of the assembly. */
export function FrontGapRow({ inst, exec, disabled }: { inst: AssemblyInstance; exec: Exec; disabled: boolean }) {
  return (
    <Row label="Fuga (grupa)">
      <MmInput
        label="Fuga između fronti"
        value={inst.frontReveal ?? DEFAULT_FRONT_REVEAL}
        min={0}
        max={20}
        disabled={disabled}
        onCommit={(v) =>
          exec('Fuga fronti', (d) => {
            const a = d.assemblies.find((x) => x.id === inst.id);
            if (a) a.frontReveal = v;
          })
        }
      />
    </Row>
  );
}

/** Front group of an assembly (outliner row "Fronte"): the shared gap. */
export function FrontGroupProperties({ inst, exec, disabled }: { inst: AssemblyInstance; exec: Exec; disabled: boolean }) {
  return (
    <div className="prop-group front-group">
      <h3>▭ Fronte ({(inst.fronts ?? []).length})</h3>
      <FrontGapRow inst={inst} exec={exec} disabled={disabled || inst.locked} />
      <p className="faint" style={{ fontSize: 11, margin: '4px 0' }}>
        Fuga je razmak između susjednih fronti (dijele pregradu ili policu). Zazor prema rubu korpusa se podešava na svakoj fronti.
      </p>
    </div>
  );
}

/** Front over openings of an assembly: mounting, reveal, thickness, hinge side, delete. */
export function FrontProperties({
  inst,
  front,
  exec,
  disabled,
  onRemoved,
  onSplit,
}: {
  inst: AssemblyInstance;
  front: Front;
  exec: Exec;
  disabled: boolean;
  onRemoved(): void;
  onSplit(direction: 'vertical' | 'horizontal'): void;
}) {
  const lock = disabled || inst.locked;
  // Drawers behind the front follow it by themselves (their boxes run to the back of the front).
  const edit = (label: string, fn: (f: Front) => void) =>
    exec(label, (d) => {
      const a = d.assemblies.find((x) => x.id === inst.id);
      const f = a?.fronts?.find((x) => x.id === front.id);
      if (!a || !f) return;
      fn(f);
    });
  const shape = frontShape(inst, front);
  const isDrawerFront = front.spaces.some((sp) => !!sp.drawer);
  const remove = () => {
    exec('Obriši frontu', (d) => {
      const a = d.assemblies.find((x) => x.id === inst.id);
      if (a) a.fronts = (a.fronts ?? []).filter((x) => x.id !== front.id);
    });
    onRemoved();
  };
  return (
    <div className="prop-group front-group">
      <h3>▭ {front.label}</h3>
      <Row label="Naziv">
        <input
          className="input"
          style={{ height: 26 }}
          defaultValue={front.label}
          key={front.label}
          disabled={lock}
          onKeyDown={(e) => e.stopPropagation()}
          onBlur={(e) => e.target.value.trim() && e.target.value !== front.label && edit('Naziv fronte', (f) => (f.label = e.target.value.trim().slice(0, 200)))}
        />
      </Row>
      <Row label="Montaža">
        <select className="input" disabled={lock} value={front.mode} onChange={(e) => edit('Montaža fronte', (f) => (f.mode = e.target.value as Front['mode']))}>
          <option value="overlay">Preklop (na ivice korpusa)</option>
          <option value="inset">Unutra (u otvoru)</option>
        </select>
      </Row>
      <Row label="Zazor">
        <MmInput label="Zazor" value={front.clearance ?? DEFAULT_FRONT_CLEARANCE} min={0} max={20} disabled={lock} onCommit={(v) => edit('Zazor fronte', (f) => (f.clearance = v))} />
      </Row>
      <FrontGapRow inst={inst} exec={exec} disabled={lock} />
      <Row label="Debljina">
        <MmInput label="Debljina fronte" value={front.thickness} min={1} max={100} disabled={lock} onCommit={(v) => edit('Debljina fronte', (f) => (f.thickness = v))} />
      </Row>
      {isDrawerFront ? (
        <Row label="Ladica">
          <span className="faint">fronta ladice — bez šarki, izvlači se</span>
        </Row>
      ) : (
        <Row label="Šarke">
          <select className="input" disabled={lock} value={front.hinge ?? ''} onChange={(e) => edit('Šarke fronte', (f) => (f.hinge = (e.target.value || null) as Front['hinge']))}>
            <option value="">— izaberite —</option>
            {HINGES.map((h) => (
              <option key={h} value={h}>
                {HINGE_LABEL[h]}
              </option>
            ))}
          </select>
        </Row>
      )}
      <p className="faint" style={{ fontSize: 11, margin: '4px 0' }}>
        Prostora: {front.spaces.length}
        {shape && ` · gabarit ${((shape.bbox.x1 - shape.bbox.x0) / 1000).toFixed(1)} × ${((shape.bbox.y1 - shape.bbox.y0) / 1000).toFixed(1)} mm`}
      </p>
      {shape?.irregular && <p className="warn-text">Nepravilan oblik — fronta ide na CNC obradu; njene mjere su posebno u krojnoj listi (tabela „CNC obrada”).</p>}
      {/* A drawer front belongs to one drawer: no split, no hinges. */}
      {!isDrawerFront && (
        <Row label="Podijeli">
          <span style={{ display: 'flex', gap: 4 }}>
            <button className="btn sm" disabled={lock} onClick={() => onSplit('vertical')} title="Dvije fronte: lijeva i desna (fuga između)">
              ┃ Lijevo / desno
            </button>
            <button className="btn sm" disabled={lock} onClick={() => onSplit('horizontal')} title="Dvije fronte: donja i gornja (fuga između)">
              ━ Gore / dolje
            </button>
          </span>
        </Row>
      )}
      <div style={{ display: 'flex', gap: 6, marginTop: 6 }}>
        <button className="btn sm ghost danger" disabled={lock} onClick={remove}>
          Obriši frontu
        </button>
      </div>
    </div>
  );
}
